import { AsyncPipe, NgClass, formatCurrency, formatDate, formatNumber } from "@angular/common";
import { AfterViewChecked, AfterViewInit, Component, EventEmitter, Input, LOCALE_ID, OnDestroy, OnInit, Output, TemplateRef, ViewChild, inject } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { MatChipListbox, MatChipOption } from "@angular/material/chips";
import { MatIcon } from "@angular/material/icon";
import { MatTooltip } from "@angular/material/tooltip";
import { Title } from "@angular/platform-browser";
import { IgxActionStripComponent, IgxCheckboxComponent, IgxForOfDirective, IgxToggleActionDirective } from "@infragistics/igniteui-angular";
import { FilteringStrategy, GridColumnDataType, IFilteringExpressionsTree, IFilteringStrategy, ISortingExpression, IgxSummaryResult, NoopSortingStrategy, SortingDirection } from '@infragistics/igniteui-angular/core';
import { IgxDropDownComponent, IgxDropDownItemComponent, IgxDropDownItemNavigationDirective } from '@infragistics/igniteui-angular/drop-down';
import { GridSelectionMode, IColumnExportingEventArgs, IGridStateOptions, IGridToolbarExportEventArgs, IRowSelectionEventArgs, IgxCellHeaderTemplateDirective, IgxCellTemplateContext, IgxColumnComponent, IgxExcelStyleClearFiltersComponent, IgxExcelStyleConditionalFilterComponent, IgxExcelStyleFilterOperationsTemplateDirective, IgxExcelStyleHeaderIconDirective, IgxGridCell, IgxGridExcelStyleFilteringComponent, IgxGridStateDirective, IgxGridToolbarComponent, IgxGridToolbarExporterComponent, IgxGridToolbarHidingComponent, IgxGridToolbarPinningComponent, IgxHeadSelectorDirective, IgxRowSelectorDirective, IgxSummaryOperand } from '@infragistics/igniteui-angular/grids/core';
import { IgxGridComponent } from '@infragistics/igniteui-angular/grids/grid';
import { IgxIconComponent } from "@infragistics/igniteui-angular/icon";
import { IgxPaginatorComponent, IgxPaginatorContentDirective } from "@infragistics/igniteui-angular/paginator";
import { ClassDirective } from "@ngbracket/ngx-layout/extended";
import { FlexDirective, LayoutAlignDirective, LayoutDirective } from "@ngbracket/ngx-layout/flex";
import { AdvancedComponentsDialogService } from "@tylertech/advanced-components-module";
import { Command, CommandBarComponent, CommandMenuComponent, LabelCommand } from "@tylertech/commands-module";
import { CommonDialogsService } from "@tylertech/common-dialogs-module";
import { CacheHelperService, OmniBarService, RepositoryService, SecurityService, SystemInfoExtended, SystemInfoService, isHttpResponseError } from "@tylertech/core-module";
import { IMenuOption, IPaginatorChangeEventData } from "@tylertech/forge";
import { ForgeBadgeModule, ForgeButtonModule, ForgeCardModule, ForgeExpansionPanelModule, ForgeIconButtonModule, ForgeIconModule, ForgeOpenIconModule, ForgePaginatorModule, ForgeTooltipModule } from "@tylertech/forge-angular";
import { IntentService } from "@tylertech/intents-module";
import { FormTitleService, GridLayoutService, AugmentedModelObject, MetadataActionHelpers, MetadataActionService, MetadataEventService, MetadataModel, MetadataPropertyService, MetadataService, RepositoryFormData, } from "@tylertech/metadata-module";
import { ODataFilterFactoryService } from "@tylertech/odata-module";
import { EventEmitterHelper, StringHelper, Utilities } from "@tylertech/shared-module";
import { IGridLayout, IGridLayoutSelector, IInfoCardData, IInfoTable, IInfoTableAction, IInfoTableCriteria, IInfoTableElement, IInfoTableRow, IMassUpdateCriteria, IMetadataAction, InfoTableActionType, PropertyMetadata } from "@tylertech/tyler-api-types";
import { BehaviorSubject, Observable, Subject, Subscription } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { InfoTableCommandArgs } from "../../interfaces";
import { InfoTableDialogService, InfoTableService } from "../../services";
import { EditRowArgs, GridRowActionsComponent, InfoGridRowActionEvent } from "./gridRowActions.component";
import { TotalCurrencySummaryOperand, TotalSummaryOperand } from "./totalSummary";


const GRID_LAYOUT_SELECTOR_TYPE = "Api.Core.EF.CommonModel.GridLayoutSelector";
const GRID_LAYOUT_TYPE = "Api.Core.EF.CommonModel.GridLayout";

export interface ServerBackedInfoTable extends IInfoTable
{
    currentPage?: number;
    pageSize?: number;
    totalRows?: number;
    /**
     * Attempt to load commands for each row as needed
     */
    lazyLoadCommands?: boolean;
}

export function isServerBackedInfoTable(obj: unknown): obj is ServerBackedInfoTable
{
    return obj
        && typeof obj === "object"
        && "totalRows" in obj;
}

/**
 * Allows custom table columns containing an icon or chip
 *
 * @deprecated - This only exists to support existing implementations in apps/purchaseCardImport
 */
export interface CustomInfoTableElement extends IInfoTableElement
{
    icon?: string;
    chip?: boolean;
}

export type AugmentedTableRow = IInfoTableRow & AugmentedModelObject;
export type InfoTableDataItem = Partial<Record<"Id", string>>
    & Record<"$$row", AugmentedTableRow>
    & Partial<Record<"$$commands", ReadonlyArray<Command>>>
    & Partial<Record<"$$commandCard", IInfoCardData>>
    & Record<string, unknown>;

interface Column
{
    // FIXME - this should be readonly....
    /* readonly */ dataType: GridColumnDataType;
    readonly name: string;
    // FIXME - this should be readonly....
    /* readonly */ label: string;
    /** @deprecated Why can't we rely on the dataType field? */
    readonly isBoolean: boolean;
    /** @deprecated Why can't we rely on the dataType field? */
    readonly isCurrency: boolean;
    readonly isFilterable: boolean;
    readonly isChip: boolean;
    readonly isIcon: boolean;
    readonly SummaryOperand: typeof IgxSummaryOperand;
}

const formattableColumnTypes: ReadonlyArray<GridColumnDataType> = [
    GridColumnDataType.Number,
    GridColumnDataType.Currency,
    GridColumnDataType.Date,
    GridColumnDataType.Time,
    GridColumnDataType.DateTime
];

/*
 * TODO - we should be able to separate these two use-cases so that we can have a infra-grid (like we have with
 * library/infra-chart) that is unaware of any of our API, and is just responsible for the grid display, and an
 * info-table that standardizes our API usages for the common cases.
 */
const enum TableLoadType
{
    /**
     * Will require the parent component to listen to events to manage the fetching and maintenance of the InfoTable.
     */
    HardCoded = "HardCoded",
    /**
     * Will use InfoTableService API to fetch and manage InfoTable object.
     */
    InfoTable = "InfoTable",
}

export interface FetchOptions
{
    readonly forceReload: boolean;
    readonly initial: boolean;
    readonly ignoreFacetReload?: boolean;
    readonly page?: number;
    readonly perPage?: number;
}

export interface GetInfoTableOptions
{
    readonly forceReload: boolean;
    readonly skip?: number;
    readonly take?: number;
    readonly orderBy?: string;
    // TODO - should we include the current FilterExpressions in some form if table filtering is enabled?
    readonly reloadFacets?: boolean;
}

export interface BulkChangeEvent
{
    readonly providerId: string;
    readonly selectedIds: ReadonlyArray<string>;
}

export interface DeleteRowArgs
{
    readonly row: AugmentedTableRow;
}

@Component({
    selector: "info-table",
    templateUrl: "./infoTable.tmpl.html",
    styleUrls: [
        "./infoTable.component.scss"
    ],
    imports: [
        AsyncPipe,
        CommandBarComponent,
        CommandMenuComponent,
        ClassDirective,
        FlexDirective,
        LayoutAlignDirective,
        LayoutDirective,
        ForgeBadgeModule,
        ForgeButtonModule,
        ForgeCardModule,
        ForgeExpansionPanelModule,
        ForgeIconButtonModule,
        ForgeIconModule,
        ForgeOpenIconModule,
        ForgePaginatorModule,
        ForgeTooltipModule,
        GridRowActionsComponent,
        IgxActionStripComponent,
        IgxCellHeaderTemplateDirective,
        IgxCheckboxComponent,
        IgxColumnComponent,
        IgxDropDownComponent,
        IgxDropDownItemComponent,
        IgxDropDownItemNavigationDirective,
        IgxExcelStyleClearFiltersComponent,
        IgxExcelStyleConditionalFilterComponent,
        IgxExcelStyleFilterOperationsTemplateDirective,
        IgxExcelStyleHeaderIconDirective,
        IgxForOfDirective,
        IgxGridComponent,
        IgxGridExcelStyleFilteringComponent,
        IgxGridStateDirective,
        IgxGridToolbarComponent,
        IgxGridToolbarExporterComponent,
        IgxGridToolbarHidingComponent,
        IgxGridToolbarPinningComponent,
        IgxHeadSelectorDirective,
        IgxIconComponent,
        IgxPaginatorComponent,
        IgxPaginatorContentDirective,
        IgxRowSelectorDirective,
        IgxToggleActionDirective,
        MatChipListbox,
        MatChipOption,
        MatIcon,
        MatTooltip,
        NgClass,
    ]
})
export class InfoTableComponent implements OnInit, OnDestroy, AfterViewChecked, AfterViewInit
{
    private readonly locale = inject(LOCALE_ID);
    private readonly $systemInfoService = inject(SystemInfoService);
    private readonly $securityService = inject(SecurityService);
    private readonly $repositoryService = inject(RepositoryService);
    private readonly $cacheHelperService = inject(CacheHelperService);
    private readonly $odataFilterFactory = inject(ODataFilterFactoryService);
    private readonly $omniBarService = inject(OmniBarService);
    private readonly $intentService = inject(IntentService);
    private readonly $commonDialogsService = inject(CommonDialogsService);
    private readonly $advancedComponentsDialogService = inject(AdvancedComponentsDialogService);
    private readonly $metadataService = inject(MetadataService);
    private readonly $metadataActionService = inject(MetadataActionService);
    private readonly $metadataEventService = inject(MetadataEventService);
    private readonly $metadataPropertyService = inject(MetadataPropertyService);
    private readonly $gridLayoutService = inject(GridLayoutService);
    private $infoTableService = inject(InfoTableService);
    private $titleService = inject(Title);
    private readonly $formTitleService = inject(FormTitleService);
    private readonly $infoTableDialogService = inject(InfoTableDialogService);

    private readonly destructor$ = new Subject<void>();

    @Input() private readonly criteria: IInfoTableCriteria;
    @Input("tableCommands") readonly customTableCommands: ReadonlyArray<Command>;
    @Input() readonly datacontroller: RepositoryFormData;
    @Input() readonly disableViewAction: boolean;
    @Input() private readonly hideHeader: boolean;
    /**
     * Required to place the grid in a loading state if the `infoTable` object is being managed by the Parent component.
     */
    @Input() isFetching: boolean;
    /**
     * If `true` forces the `TableLoadType` to `Search`.
     *
     * @deprecated We should have some more abstract way to defining this behavioral difference than by forcing contextual information down to this level.
     */
    @Input() readonly isSearchTable: boolean;
    /**
     * If provided, this value will be used with the `providerId` from the `infoTable` object, or the `criteria` object, as
     * the complete `providerId` for this table. The constructed providerId will be formatted as `<providerId>.<key>`.
     */
    @Input() readonly key?: string;
    @Input() private readonly noFilter: boolean;
    @Input() private readonly rowsPerPage: number;
    /**
     * Only used if `useDataSearch` is set to true.
     * Commands are added to the grid's toolbar.
     */
    @Input() readonly searchCommands: ReadonlyArray<Command>;

    /**
     * Moves the paginator to the side if set to `true`
     */
    @Input() readonly spaceForFab: boolean;

    /**
     * Remove's the tcwCard's outline and removes the advanced grid functions; hide and pin columns, export, and layout.
     *
     * Used in:
     * apps\glaccounts\src\components\manager\manager\manager.tmpl.html
     *   30,29:                 <info-table [standAlone]="true" [criteria]="segmentCriteria" [hasParent]="true"
     *   36,29:                             [standAlone]="true" [noFilter]="true"
     *
     * apps\glaccounts\src\components\manager\manager\transactions.tmpl.html
     *   5,29:                 <info-table [standAlone]="true" [criteria]="transactionCriteria" [displayHeader]="false"
     *   12,29:                 <info-table [standAlone]="true" [criteria]="encumbranceCriteria" [displayHeader]="false"
     *   19,29:                 <info-table [standAlone]="true" [criteria]="reserveCriteria" [displayHeader]="false"
     *
     * @deprecated rename this to something meaningful.
     */
    @Input() readonly standAlone: boolean;

    /**
     * Allows the parent component to trigger a data refetch for `TableLoadType.InfoTable` instances without needing to
     * know implementation specifics for the data.
     */
    @Input() set fireTableReload(value: Observable<void>)
    {
        this._fireTableReload?.unsubscribe();
        if (!value)
        {
            return;
        }

        this.isFetching = true;
        this._fireTableReload = value
            .pipe(takeUntil(this.destructor$))
            .subscribe(() =>
            {
                this.fetch({
                    forceReload: true,
                    initial: false,
                    page: this.currentPage,
                    perPage: this.perPage
                });
            });
    }

    //  TODO - change binding name to match field name.
    @Input("tableData") get infoTable(): IInfoTable { return this._infoTable; }
    set infoTable(value: IInfoTable)
    {
        this._infoTable = value;

        if (this._infoTable && this.tableLoadType === TableLoadType.HardCoded)
        {
            this.processHardCodedTable(false);
        }
    }

    @Output("tableDataChange") private readonly infoTableChange = new EventEmitter<IInfoTable>();


    /**
     * Overrides default behavior of showing the EditDialog and refetching the infoTable afterwards.
     */
    @Output() private readonly add = new EventEmitter<void>();
    @Output("clientAction") private readonly tableAction = new EventEmitter<InfoTableCommandArgs>();
    @Output() private readonly bulkChange = new EventEmitter<BulkChangeEvent>();
    // TODO - what is the difference between customCommand and customAction?
    /** @deprecated only used in apps/purchaseCardImport */
    @Output() private readonly customCommand = new EventEmitter<InfoTableCommandArgs>();
    // TODO - what is the difference between customCommand and customAction?
    /** @deprecated Seems unused */
    @Output() private readonly customAction = new EventEmitter<InfoGridRowActionEvent>();
    @Output() private readonly selectionChange = new EventEmitter<Array<string>>();
    /**
     * Used for `HardCoded` TableLoadType scenarios so that parent component's can respond when the infoTable needs to be reloaded.
     * This event will not be emitted for standard `InfoTable` and `Search` `TableLoadType`s.
     */
    @Output() private readonly reloadTableData = new EventEmitter<GetInfoTableOptions>();
    /**
     * Overrides default behavior of showing the EditDialog and refetching the infoTable afterwards.
     */
    @Output() private readonly editRow = new EventEmitter<EditRowArgs>();
    /**
     * Overrides default behavior of sending a HTTP request to InfoTableService/PerformAction with a standard `Delete` command.
     */
    @Output() private readonly deleteRow = new EventEmitter<DeleteRowArgs>();

    @ViewChild("booleanTemplate") private readonly booleanTemplate: TemplateRef<IgxCellTemplateContext>;
    @ViewChild("chipTemplate") private readonly chipTemplate: TemplateRef<IgxCellTemplateContext>;
    @ViewChild("grid") private readonly grid: IgxGridComponent;
    @ViewChild("gridLayoutDropdown") private readonly gridLayoutDropdown: IgxDropDownComponent;
    @ViewChild("iconTemplate") private readonly iconTemplate: TemplateRef<IgxCellTemplateContext>;
    @ViewChild(IgxGridStateDirective, { static: true }) private readonly state: IgxGridStateDirective;
    @ViewChild("toolbar") private readonly toolbar: IgxGridToolbarComponent;
    @ViewChild("toolbarExporter") private readonly toolbarExporter: IgxGridToolbarExporterComponent;

    private readonly initialWidths: Record<string, string> = {};
    private readonly columnQuickFilters: Record<string, string> = {};

    // TODO - does this _have_ to be backed by a Subject|Observable, we pipe it through the AsyncPipe, so the IgxGrid doesn't want an observable
    private readonly gridDataSubject = new BehaviorSubject<Array<unknown>>([]);
    private readonly _prevRequest: Subscription;

    private _infoTable: IInfoTable;
    private _currentPage: number = 0;
    private _fireTableReload?: Subscription;
    private _lastFilterColumnField: string;
    private _perPage: number = 0;
    private _selectedIds: Set<string>;
    private allSelected: boolean;
    private allDeselected: boolean;
    private defaultLayout: string;
    private gridLayoutId: string;
    /** @deprecated Why do we have this? */
    private internalInfoTable: IInfoTable;
    private layoutLoaded: boolean;
    private recalculating: boolean;
    /** @deprecated What is this for? */
    private resetRowSelection: boolean;
    /**
     * The tableLoadType is determined using this component instance's initial data-binding.
     *
     * If an `infoTable` (binding name `tableData`) object was given, then we will assume the parent component will be responsible for any and all
     * update to the `infoTable` object as whole. @see TableLoadType.HardCoded
     *
     * If a `criteria` object was given, then we will manage the infoTable for the parent component using the
     * `InfoTableService` API. @see TableLoadType.InfoTable
     */
    private tableLoadType: TableLoadType;

    readonly loadFailed: boolean;
    readonly loadFailedMessage: string;
    readonly selectOptions: Array<number> = [5, 15, 25, 50, 100];
    readonly filterStrategy: IFilteringStrategy = FilteringStrategy.instance();
    readonly noopSortStrategy = NoopSortingStrategy.instance();
    readonly cellSelectionMode: GridSelectionMode = GridSelectionMode.none;

    readonly layoutOptions: Array<IMenuOption> = [
        {
            value: "load",
            label: "Load",
        },
        {
            value: "save",
            label: "Save",
        },
        {
            value: "delete",
            label: "Delete",
        }
    ];

    /** Given to the IgxGridComponent in the template. */
    readonly options: Readonly<IGridStateOptions> = {
        cellSelection: true,
        rowSelection: true,
        filtering: true,
        advancedFiltering: true,
        paging: true,
        sorting: true,
        groupBy: true,
        columns: true,
        expansion: true,
        rowPinning: true,
        columnSelection: true,
    };

    columns: ReadonlyArray<Column>;
    enableLayout: boolean;

    selectionMode: GridSelectionMode = GridSelectionMode.none;
    tableCommands: ReadonlyArray<Command>;
    tableOptionsOpen: boolean;

    constructor()
    {
        const $metadataEventService = this.$metadataEventService;

        // TODO - do we really care? or is this from before the metadata got a independent implementation of these grids...
        $metadataEventService.onRecalculateWorking.pipe(takeUntilDestroyed())
            .subscribe(args =>
            {
                this.recalculating = args.Recalculating;
            });
    }

    private get infoTableHasCommands(): boolean
    {
        return this.tableCommands?.length > 0;
    }

    private get currentFilterColumnField(): string
    {
        if (this.grid?.excelStyleFilteringComponent?.column)
        {
            this._lastFilterColumnField = this.grid?.excelStyleFilteringComponent?.column.field;
        }
        return this._lastFilterColumnField;
    }

    private get columnFilterText(): string
    {
        return this.currentFilterColumnField
            ? this.columnQuickFilters[this.currentFilterColumnField]
            : undefined;
    }

    private set columnFilterText(value: string)
    {
        if (!this.currentFilterColumnField)
        {
            return;
        }
        this.columnQuickFilters[this.currentFilterColumnField] = value;
    }

    private get selectedIds(): Set<string> { return this._selectedIds; }
    private set selectedIds(value: Set<string>)
    {
        this._selectedIds = new Set<string>(value);
        this.selectionChange.emit(Array.from(this._selectedIds));
    }

    private get singleCommand(): boolean
    {
        const expectedNumberOfCommands = this.infoTable?.MassUpdate ? 2 : 1;
        return this.infoTableHasCommands
            && this.infoTable?.Commands?.filter(x => x.Visible).length === expectedNumberOfCommands;
    }

    get hasCommandBar(): boolean { return !this.isSearchTable && this.singleCommand && this.hasSelection; }
    get hasCommandMenu(): boolean
    {
        return this.infoTableHasCommands && !this.isSearchTable && !this.singleCommand && this.hasSelection;
    }

    get hasCustomAction(): boolean { return EventEmitterHelper.hasSubscribers(this.customAction); }

    get hasSelection(): boolean
    {
        return this.infoTable && this.grid
            && this.grid.selectionService.rowSelection.size > 0;
    }

    get allowMultipleSelection(): boolean { return this.selectionMode === GridSelectionMode.multiple; }
    get allowSort(): boolean { return !this.infoTable?.DisableHeaderSort; }
    get useColumnFiltering(): boolean { return !this.noFilter; }
    get showTitle(): boolean { return this.infoTable?.Title && !this.hideHeader; }
    get title(): string { return this.infoTable?.Title; }
    get canAdd(): boolean { return this.infoTable?.CanAdd && !this.showMultiSelectCommands; }
    // TODO - does this need to be backed by a Subject|Observable, we pipe it through the AsyncPipe, so the IgxGrid doesn't want an observable
    get gridDataObservable(): Observable<Array<unknown>> { return this.gridDataSubject.asObservable(); }
    get showMultiSelectCommands(): boolean
    {
        return this.selectionMode === GridSelectionMode.multiple
            && this.hasSelection
            && !this.infoTable?.SelectOnly;
    }

    get isViewOnly(): boolean { return this.infoTable?.ViewOnly || this.recalculating; }
    get hasSearchCommands(): boolean { return this.searchCommands?.length > 0; }
    get hasTableCommands(): boolean { return this.customTableCommands?.length > 0; }
    get providerId(): string
    {
        const id = this.infoTable?.ProviderId || this.criteria?.ProviderId;
        return this.key
            ? `${id}.${this.key}`
            : id;
    }

    // TODO - invert the meaning of this so we don't have to negate in the template
    get enableMultiSelect(): boolean
    {
        if (!this.infoTable || !this.grid)
        {
            return false;
        }

        const enable = (this.infoTableHasCommands
            && this.infoTable?.Commands.filter(x => x.Visible).length > 0)
            || this.infoTable?.SelectOnly
            || this.infoTable?.MassUpdate
            || this.hasSearchCommands;

        // TODO - why are we causing side-effects here?
        this.selectionMode = enable
            ? GridSelectionMode.multiple
            : GridSelectionMode.none;

        return enable;
    }

    get totalRecords(): number
    {
        return isServerBackedInfoTable(this.infoTable)
            ? this.infoTable.totalRows
            : this.infoTable?.Rows.length || 0;
    }

    get perPage(): number { return isServerBackedInfoTable(this.infoTable) ? this.infoTable.pageSize : this._perPage; }
    set perPage(value: number) { this._perPage = value; }

    get currentPage(): number { return isServerBackedInfoTable(this.infoTable) ? this.infoTable.currentPage : this._currentPage; }
    set currentPage(value: number) { this._currentPage = value; }

    get cardStyles(): Record<string, string>
    {
        return {
            height: !this.standAlone ? "100%" : undefined,
            // eslint-disable-next-line @typescript-eslint/naming-convention
            "max-height": !this.standAlone ? "90vh" : undefined,
        };
    }

    get gridClasses(): Record<string, boolean>
    {
        return {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            "grid-min-height": !this.standAlone,
        };
    }

    ngOnDestroy(): void
    {
        this._prevRequest?.unsubscribe();

        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        if (this.rowsPerPage && this.selectOptions.indexOf(this.rowsPerPage) < 0)
        {
            this.selectOptions.push(this.rowsPerPage);
        }

        this.perPage = this.rowsPerPage || 25;

        this.tableLoadType = this.getTableLoadType();

        this.isFetching = this.tableLoadType === TableLoadType.HardCoded;
        if (this.tableLoadType === TableLoadType.HardCoded
            && !EventEmitterHelper.hasSubscribers(this.reloadTableData))
        {
            console.warn(`Hard-coded infoTable requires a reloadTableData event subscription.`);
        }
    }

    ngAfterViewInit(): void
    {
        if (this.tableLoadType === TableLoadType.InfoTable)
        {
            this.fetch({
                forceReload: true,
                initial: true,
                page: this.currentPage,
                perPage: this.perPage
            });
        }
        else if (this.infoTable)
        {
            this.processHardCodedTable(true);
        }
    }

    ngAfterViewChecked(): void
    {
        // TODO - What is this for? And why are we doing it?
        this.grid.nativeElement
            .querySelectorAll(".toolbar-icon-container--action .igx-button--outlined")
            .forEach((x: HTMLElement) =>
            {
                x.title = "";
            });
    }

    onFilterChange(_$event: IFilteringExpressionsTree): void
    {
        this.currentPage = 0;
        this.fetch({
            forceReload: this.tableLoadType === TableLoadType.HardCoded,
            initial: false,
            page: this.currentPage,
            perPage: this.perPage
        });
    }

    onSortingChange(_$event: ReadonlyArray<ISortingExpression>): void
    {
        this.currentPage = 0;
        this.fetch({
            forceReload: this.tableLoadType === TableLoadType.HardCoded,
            initial: false,
            ignoreFacetReload: false,
            page: this.currentPage,
            perPage: this.perPage
        });

        this.inPlaceSort(this.infoTable.Rows);
    }

    private getTableLoadType(): TableLoadType | undefined
    {
        if (this.criteria)
        {
            return TableLoadType.InfoTable;
        }

        return TableLoadType.HardCoded;
    }

    onPageChange($event: Event): void
    {
        const pageChangeEvent = ($event as CustomEvent<IPaginatorChangeEventData>).detail;
        if (this.isFetching
            || (this.currentPage === pageChangeEvent.pageIndex && this.perPage === pageChangeEvent.pageSize))
        {
            return;
        }

        this.grid.crudService.endEditMode();
        this.grid.clearCellSelection();
        this.grid.deselectAllRows(false);

        this.currentPage = pageChangeEvent.pageIndex;
        this.perPage = pageChangeEvent.pageSize;

        this.fetch({
            forceReload: false,
            initial: false,
            ignoreFacetReload: false,
            page: pageChangeEvent.pageIndex,
            perPage: pageChangeEvent.pageSize
        });
    }

    doRowAction($event: InfoGridRowActionEvent): void
    {
        const command = $event.command;
        const item = $event.item;
        if (!this.hasCustomAction)
        {
            return;
        }

        this.customAction.emit({
            command: command,
            item: item,
        });
    }

    doCustomCommand($event: InfoTableCommandArgs): void
    {
        this.customCommand.emit($event);
    }

    private fetch({ forceReload, initial, ignoreFacetReload, page, perPage }: FetchOptions): void
    {
        const skip = (page * perPage) || 0;
        const take = perPage || this.perPage;
        if (this.tableLoadType === TableLoadType.HardCoded)
        {
            this.reloadTableData.emit({
                forceReload,
                reloadFacets: !ignoreFacetReload,
                skip,
                take,
                orderBy: this.transformSortingExpressions(),
            });
            return;
        }

        this.isFetching = true;
        this.currentPage = initial && !this.resetRowSelection
            ? 0
            : page;
        this.perPage = perPage;

        this.resetRowSelection = false;

        this.getInfoTable(forceReload)
            .then(
                (infoTable: IInfoTable) =>
                {
                    this.infoTable = infoTable;
                    if (!this.$titleService.getTitle())
                    {
                        this.$titleService.setTitle(infoTable.Title);
                        this.$formTitleService.setTitle(infoTable.Title);
                    }
                    this.processInfoTable(infoTable, initial);
                },
                (error: unknown) =>
                {
                    console.error(`Error loading and processing infoTable data`, error);
                })
            .finally(() =>
            {
                this.isFetching = false;
            });
    }

    private processHardCodedTable(isInitial: boolean): void
    {
        this.getHardCodedInfoTableData()
            .then((infoTable: IInfoTable) =>
            {
                this.processInfoTable(infoTable, isInitial);
            })
            .finally(() =>
            {
                this.isFetching = false;
            });
    }

    private transformSortingExpressions(): string
    {
        if (!this.grid.sortingExpressions)
        {
            return undefined;
        }

        const expressions: Array<string> = this.grid.sortingExpressions
            .map(e =>
            {
                const field = this.infoTable.Rows[0].Elements
                    .find(x => x.NameForGrid === e.fieldName ||
                        Utilities.IsNullOrUndefined(x.NameForGrid) && x.Name === e.fieldName).Name;

                return `${field} ${e.dir === SortingDirection.Desc ? "DESC" : "ASC"}`;
            });
        return expressions.join(",");
    }

    private processInfoTable(infoTable: IInfoTable, isInitial?: boolean): void
    {
        const rows = infoTable.Rows;

        // TODO - We can't infer a column's data type if the first row's element is empty, Why wasn't this something defined by the backend?
        const infoTableColumns: Array<Column> = infoTable.Rows?.length > 0
            ? infoTable.Rows[0].Elements.reduce(
                (columns: Array<Column>, element: CustomInfoTableElement, colIndex: number) =>
                {
                    if (!element.Visible)
                    {
                        return columns;
                    }

                    const dataType = this.getGridColumnDataType(element);
                    const column: Column = {
                        name: this.getColumnName(element, colIndex),
                        label: element.Label || "",
                        dataType: dataType,
                        isCurrency: dataType === GridColumnDataType.Currency,
                        isBoolean: dataType === GridColumnDataType.Boolean,
                        isFilterable: true,
                        isIcon: !!element.icon,
                        isChip: element.chip,
                        SummaryOperand: dataType === GridColumnDataType.Currency
                            ? TotalCurrencySummaryOperand
                            : TotalSummaryOperand
                    };
                    columns.push(column);
                    return columns;
                },
                [])
            : [];

        const infoTableItems = rows.map(
            (row) =>
            {
                return this.convertToTableItem(row, infoTableColumns);
            });

        // TODO - why aren't we just always replacing the columns array here?
        if (!this.columns || this.columns.length === 0)
        {
            this.columns = infoTableColumns;
        }
        else
        {
            this.columns.forEach(existingColumn =>
            {
                const newColumn = infoTableColumns.find(x => x.name === existingColumn.name);
                if (newColumn)
                {
                    // TODO -  Are we really just assuming that the label is the only thing that could change between instances?
                    existingColumn.label = newColumn.label;
                }
            });
        }

        this.registerCommands(infoTable);

        this.gridDataSubject.next(infoTableItems);
        this.infoTableChange.emit(infoTable);
        if (!infoTable)
        {
            return;
        }

        if (isInitial)
        {
            if (!this.layoutLoaded)
            {
                this.initializeLayout();
            }

            this.allSelected = false;
            this.allDeselected = false;
        }

        // TODO - Why do we need a timeout here?
        setTimeout(() =>
        {
            this.selectedIds = new Set<string>(
                rows
                    ?.filter(x => x.Selected === true)
                    .map(m => m.Id)
            );

            if (!this.selectedIds)
            {
                return;
            }

            const selectedIds = Array.from(this.selectedIds);
            if (this.allSelected)
            {
                this.grid.selectRows(selectedIds, true);
            }
            else if (this.allDeselected)
            {
                this.grid.selectionService.rowSelection.clear();
            }
            else if (this.selectedIds)
            {
                this.grid.selectRows(selectedIds, true);
            }
        }, 5);
    }

    private initializeLayout(): void
    {
        this.$systemInfoService.GetCachedSystemInfo()
            .then((systemInfo: SystemInfoExtended) =>
            {
                return !systemInfo.IsTest() && !!this.gridLayoutId;
            })
            .then((enabled: boolean): PromiseLike<unknown> | string | undefined =>
            {
                this.enableLayout = enabled;
                if (!this.enableLayout)
                {
                    return;
                }

                if (this.defaultLayout)
                {
                    return this.defaultLayout;
                }

                const odata = this.$odataFilterFactory.createBuilderFor<IGridLayout>()
                    .filterEQ("IsDefault", true)
                    .filterEQ("GridId", this.gridLayoutId)
                    .constantFilter(`UserId eq '${Utilities.escapeODataString(this.$securityService.principle.UserId)}'`);
                return this.$repositoryService.Fetch(GRID_LAYOUT_TYPE, odata.build());
            })
            .then((result: unknown | undefined) =>
            {
                if (!result)
                {
                    return;
                }

                if (Array.isArray(result) && result.length > 0)
                {
                    this.defaultLayout = result[0].Layout;
                    return result[0].Layout;
                }

                this.defaultLayout = "";
            })
            .then((layout: unknown | undefined) =>
            {
                if (layout)
                {
                    this.layoutLoaded = true;
                    this.state.setState(layout);
                }
            });
    }

    getColumnFormatter(column: Column): (value: unknown) => string
    {
        return (value: unknown) =>
        {
            return this.formatColumnValue(column, value);
        };
    }

    private formatColumnValue(dataColumn: Column, value: unknown): string
    {
        if (!value)
        {
            return "";
        }

        if (!dataColumn || !formattableColumnTypes.includes(dataColumn.dataType))
        {
            return value.toString();
        }

        const numericValue = typeof value === "number"
            ? value
            : StringHelper.parseNumber(value.toString());
        switch (dataColumn.dataType)
        {
            case GridColumnDataType.Number:
                {
                    // TODO - is there some way we can define precision and scale for the column?
                    const formattedValue = formatNumber(Math.abs(numericValue), this.locale, "1.2");
                    // TODO - do we want non-currency numbers to be parenthesized?
                    if (numericValue < 0)
                    {
                        return `(${formattedValue})`;
                    }
                    return formattedValue;
                }
            case GridColumnDataType.Currency:
                {
                    const formattedValue = formatCurrency(Math.abs(numericValue), this.locale, "$", "1.2");
                    if (numericValue < 0)
                    {
                        return `(${formattedValue})`;
                    }
                    return formattedValue;
                }
            case GridColumnDataType.Date:
                return formatDate(value.toString(), "MM/dd/yyyy", this.locale);
            case GridColumnDataType.Time:
                return formatDate(value.toString(), "h:mm a", this.locale);
            case GridColumnDataType.DateTime:
                return formatDate(value.toString(), "MM/d/yyyy h:mm a", this.locale);
            default:
                return "";
        }
    }

    getColumnWidth(column: Column): string
    {
        if (this.initialWidths[column.name] !== undefined)
        {
            return this.initialWidths[column.name];
        }
        const gridColumn = this.grid.columns.find(x => x.field === column.name);
        if (!gridColumn)
        {
            return "";
        }

        if (this.layoutLoaded)
        {
            this.initialWidths[column.name] = gridColumn.width;
            return this.initialWidths[column.name];
        }

        this.initialWidths[column.name] = this.grid.columns.length > 1 ? undefined : "100%";

        return this.initialWidths[column.name];
    }

    isColumnHidden(column: Column): boolean
    {
        if (!this.infoTable?.Rows?.length)
        {
            return false;
        }

        return 0 === this.infoTable?.Rows
            .filter((row: IInfoTableRow & AugmentedModelObject) =>
            {
                const element = row.Elements.find(x => x.Name === column.name);
                return !element
                    || !this.$metadataService.GetPermissionDenied(!element.Visible, element.HiddenProperty, row.$$model);
            }).length;
    }

    hasSummary(column: Column): boolean
    {
        if (this.infoTable?.Rows.length === 0)
        {
            return false;
        }

        const el = this.infoTable?.Rows[0].Elements.find(x => x.Name === column.name);
        return el?.HasTotal || false;
    }

    private registerCommands(infoTable: IInfoTable): void
    {
        const infoTableHasCommands = infoTable.Commands?.length > 0;
        if (!infoTableHasCommands && !infoTable.MassUpdate)
        {
            return;
        }

        const commands = infoTableHasCommands
            ? infoTable.Commands
                .map(cmd =>
                {
                    return new LabelCommand({
                        label: cmd.Label,
                        tooltip: cmd.Label,
                        action: $event => this.performTableCommand(cmd, $event),
                        getEnabled: () => cmd.Enabled,
                        getVisible: () => cmd.Visible
                    });
                })
            : [];

        commands.push(
            new LabelCommand({
                label: "Mass Update",
                tooltip: "Mass Update",
                action: () => this.massUpdate(),
                getEnabled: () => this.infoTable?.CanEdit,
                getVisible: () => this.infoTable?.MassUpdate
            }));

        this.tableCommands = commands;
    }

    private performTableCommand(action: IInfoTableAction, $event?: Event): void
    {
        $event?.stopPropagation();

        const rows = this.infoTable?.Rows.filter(x => this.grid.selectionService.rowSelection.has(x.Id));
        const actionType: InfoTableActionType = action.ActionType;
        switch (actionType)
        {
            case InfoTableActionType.Client:
                this.tableAction.emit({
                    command: action,
                    rows: rows,
                });
                break;
            case InfoTableActionType.Server:
                this.$infoTableService.PerformAction(
                    {
                        ProviderId: this.providerId,
                        Action: action,
                        Rows: rows,
                    })
                    .then(_x =>
                    {
                        this.fetch({
                            forceReload: true,
                            initial: true,
                            page: this.currentPage,
                            perPage: this.perPage
                        });
                    });
                break;
            case InfoTableActionType.Metadata:
                {
                    const metadataAction: IMetadataAction = StringHelper.parse(action.Data[0]);
                    metadataAction.Context = undefined;
                    const repositoryForm = this.datacontroller || new RepositoryFormData(undefined, undefined);
                    this.$metadataActionService.ExecuteAction(
                        rows.map(x => x.Id), metadataAction, repositoryForm)
                        .then(_x =>
                        {
                            this.grid.selectionService.rowSelection.clear();
                            this.infoTable?.Rows.forEach(x => (x.Selected = false));
                            this.selectedIds = new Set<string>();
                        });
                    break;
                }
            default:
                {
                    const parms: Record<string, unknown> = {};
                    action.Data.forEach((value, key) =>
                    {
                        if (key % 2 === 0)
                        {
                            parms[value] = action.Data[key + 1];
                        }
                    });
                    this.$intentService.resolve(action.MimeType, action.Action, parms);
                    break;
                }
        }
    }

    private massUpdate(): void
    {
        if (EventEmitterHelper.hasSubscribers(this.bulkChange))
        {
            this.bulkChange.emit({
                providerId: this.providerId,
                selectedIds: Array.from(this.selectedIds)
            });
            return;
        }

        this.$advancedComponentsDialogService.showMassUpdate(
            {
                providerId: this.providerId,
                entityIds: Array.from(this.grid.selectionService.rowSelection),
                localUpdate: false
            })
            .then(
                (_criteria: IMassUpdateCriteria) =>
                {
                    this.fetch({
                        forceReload: true,
                        initial: true,
                        page: this.currentPage,
                        perPage: this.perPage
                    });
                },
                _e =>
                {
                    /* Squashing errors */
                }
            );
    }

    addItem(): void
    {
        if (EventEmitterHelper.hasSubscribers(this.add))
        {
            this.add.emit();
            return;
        }

        // TODO - why are setting this? do we want to hide the table data that is behind the dialog?
        this.isFetching = true;
        const criteria: IInfoTableCriteria = {
            ProviderId: this.providerId,
            ParentId: this.criteria.Id,
            Criteria: undefined,
            Id: undefined,
        };
        this.$infoTableDialogService.showEdit(
            {
                criteria,
                allowAttachments: this.infoTable?.AllowAttachments,
                allowNotes: this.infoTable?.AllowNotes,
                preventSaveAndNew: this.infoTable?.PreventSaveAndNew,
                entityName: this.infoTable?.EntityName,
                hideNoteSubject: this.infoTable?.HideNoteSubject,
                viewOnly: false,
                canDelete: false,
                infoTable: undefined,
            })
            .then(() =>
            {
                this.fetch({
                    forceReload: true,
                    initial: true,
                    page: this.currentPage,
                    perPage: this.perPage
                });
            });
    }

    areAllSelected(selected: number): boolean
    {
        const totalSelectable = this.infoTable?.Rows.length;
        return selected >= totalSelectable;
    }

    onSelectHeader(event: Event, selected: number): void
    {
        event.stopPropagation();
        event.preventDefault();

        const newSelection = this.infoTable?.Rows.map(m => m.Id);

        if (this.areAllSelected(selected))
        {
            this.grid.deselectAllRows();
            newSelection.forEach(x => this.applySelection(x, false));
            this.allDeselected = true;
            this.allSelected = false;
        }
        else
        {
            this.grid.selectRows(newSelection);
            newSelection.forEach(x => this.applySelection(x, true));
            this.allSelected = true;
            this.allDeselected = false;
        }

        this.selectedIds = new Set<string>(newSelection);
        this.resetRowSelection = true;
    }

    onSelectRow(event: IRowSelectionEventArgs): void
    {
        const added = event.added;
        if (added.length === 0 && event.removed.length === 0)
        {
            //clicked a disabled selection, bail out
            return;
        }

        event.removed.forEach(x => this.applySelection(x, false));
        added.forEach(x => this.applySelection(x, true));

        this.selectedIds = new Set<string>(event.newSelection.map(m => m.Id));
        this.resetRowSelection = true;
    }

    private applySelection(id: unknown, selected: boolean): void
    {
        const row = this.infoTable?.Rows.find(i => i.Id === id);
        if (row)
        {
            row.Selected = selected;
        }
    }

    getIcon(cell: IgxGridCell): string
    {
        const element = this.getElement(cell);
        return element.icon;
    }

    useIconSpan(cell: IgxGridCell): boolean
    {
        const element = this.getElement(cell);
        return element.icon.startsWith("icon-");
    }

    private getRow(cell: IgxGridCell): IInfoTableRow & AugmentedModelObject
    {
        const id = cell.row.data[cell.grid.primaryKey];
        return this.infoTable.Rows.find(x => x.Id === id);
    }

    private getElement(cell: IgxGridCell): CustomInfoTableElement
    {
        const row = this.getRow(cell);
        const field = cell.column.field;
        return row.Elements.find(x => x.Name === field);
    }

    getSummaryOperand(column: Column): typeof IgxSummaryOperand
    {
        return column.isCurrency ? TotalCurrencySummaryOperand : TotalSummaryOperand;
    }

    onToolbarExporting($event: IGridToolbarExportEventArgs): void
    {
        this.toolbar.showProgress = true;

        if ($event.cancel)
        {
            this.toolbar.showProgress = false;
            return;
        }

        $event.exporter.columnExporting
            .pipe(takeUntil(this.destructor$))
            .subscribe((args: IColumnExportingEventArgs) =>
            {
                const header = this.infoTable.Rows[0].Elements.find(x => x.Name === args.header)?.Label;
                if (header)
                {
                    args.header = header;
                }
            });

        this.getInfoTable(false)
            .then((tableData: IInfoTable) =>
            {
                const data = tableData.Rows.reduce(
                    (data, row) =>
                    {
                        const item: unknown = {};
                        if (!this.grid.columns.find(c => c.field === "Id")?.hidden)
                        {
                            item["Id"] = row.Id;
                        }

                        this.grid.columns.forEach(column =>
                        {
                            const element = row.Elements.find(x => x.Visible && x.Name === column.field);
                            if (!element)
                            {
                                return;
                            }

                            const nameForGrid = element.NameForGrid || element.Name;
                            const columnToExport = this.grid.columns.find(c => c.field === nameForGrid);
                            if (columnToExport && !columnToExport.hidden)
                            {
                                item[nameForGrid] = element.Value;
                            }
                        });

                        data.push(item);

                        return data;
                    },
                    []);

                $event.options.fileName = tableData.Title ? `${tableData.Title} Export` :
                    this.infoTable.Title
                        ? this.infoTable.Title
                        : this.$omniBarService.title;
            })
            .finally(() =>
            {
                this.toolbar.showProgress = false;
            });
    }

    getSummaryFormatter(): (summary: IgxSummaryResult, operand: IgxSummaryOperand) => string
    {
        const self = this;
        return summaryColumnFormatter;

        function summaryColumnFormatter(summary: IgxSummaryResult, operand: IgxSummaryOperand): string
        {
            const result = summary.summaryResult;
            // TODO - are negative values supposed to be parenthesized? @see `formatColumnValue`
            return operand instanceof TotalCurrencySummaryOperand
                ? formatCurrency(result, self.locale, "$", "1.2")
                : result;
        }
    }

    clearQuickFilter(): void
    {
        this.columnFilterText = undefined;
    }

    onLayoutSelectionChanging(_e: unknown): void
    {
        const action = this.gridLayoutDropdown.selectedItem ? this.gridLayoutDropdown.selectedItem.value : "";
        switch (action)
        {
            case "load":
                this.restoreGridState();
                break;
            case "save":
                this.saveGridState();
                break;
            case "delete":
                this.deleteGridState();
                break;
        }
        this.gridLayoutDropdown.clearSelection();
    }

    private saveGridState(): void
    {
        const gridState = this.state.getState(true);
        const layout = typeof gridState === "string"
            ? gridState
            : JSON.stringify(gridState);
        const gridLayout: IGridLayout & MetadataModel = {
            UserId: this.$securityService.principle.UserId,
            GridId: this.gridLayoutId,
            Layout: layout,
            IsDefault: false,
            IsPublic: false,
            SerializedRowState: "Added",
        };

        const propertyMetadata: PropertyMetadata = {
            Name: "SaveLayout",
            RecalculateOnChange: ["UserId", "GridId", "Layout"],
            RecalculateWithParent: false
        };
        // TODO - Why are we using a recalc to do what should be a repository.Save call?
        this.$metadataPropertyService.recalculateProperties(
            GRID_LAYOUT_TYPE,
            propertyMetadata,
            gridLayout,
            undefined,
            true,
            undefined,
            false,
            false,
            new RepositoryFormData(GRID_LAYOUT_TYPE, gridLayout));
    }

    private restoreGridState(): void
    {
        const gridLayoutSelector: IGridLayoutSelector = {
            UserId: this.$securityService.principle.UserId,
            GridId: this.gridLayoutId,
            OwnLayout: true,
            Filter: undefined,
            Layout: undefined,
            SelectedLayout: undefined,
        };
        this.$gridLayoutService.LoadLayoutAction(gridLayoutSelector)
            .then((action: IMetadataAction) =>
            {
                const formRepository = new RepositoryFormData(GRID_LAYOUT_SELECTOR_TYPE, gridLayoutSelector);
                return this.$metadataActionService.ExecuteAction(gridLayoutSelector, action, formRepository);
            })
            .then((model: unknown) =>
            {
                if (MetadataActionHelpers.IsCancelObject(model))
                {
                    return;
                }

                return (model as IGridLayoutSelector).Layout.Layout;
            })
            .then(layout =>
            {
                this.state.setState(layout);
            });
    }

    private deleteGridState(): void
    {
        const gridLayoutSelector: IGridLayoutSelector = {
            UserId: this.$securityService.principle.UserId,
            GridId: this.gridLayoutId,
            OwnLayout: true,
        };

        let selectedLayoutPropertyMetadata: PropertyMetadata;

        this.$gridLayoutService.LoadLayoutAction(gridLayoutSelector)
            .then((action: IMetadataAction) =>
            {
                //  TODO - why isn't the label set before we get it here?
                action.Label = "Delete Layout";
                selectedLayoutPropertyMetadata = action.ViewToShow.ViewMetaData.Properties
                    .find(x => x.Name === "SelectedLayout");
                const repositoryForm = new RepositoryFormData(GRID_LAYOUT_SELECTOR_TYPE, gridLayoutSelector);
                return this.$metadataActionService.ExecuteAction(
                    gridLayoutSelector, action, repositoryForm);
            })
            .then((model: unknown) =>
            {
                if (MetadataActionHelpers.IsCancelObject(model))
                {
                    return;
                }

                // TODO - probably needs an actual TypeGuard since there is no guarantee that the actionService gave us what we think we want...
                const gridLayout = model as IGridLayoutSelector;

                gridLayout.Layout.SerializedRowState = "Deleted";
                return this.$repositoryService.Save(
                    {
                        Item1: GRID_LAYOUT_TYPE,
                        Item2: StringHelper.stringify(gridLayout.Layout),
                    });
            })
            .then((result: unknown) =>
            {
                if (!result)
                {
                    return;
                }

                this.$cacheHelperService.clearAllForType(selectedLayoutPropertyMetadata?.Lookup);
            });
    }

    onColumnInit(columnComponent: IgxColumnComponent): void
    {
        if (!this.columns)
        {
            return;
        }

        const column = this.columns.find(x => x.name === columnComponent.field);
        if (!column)
        {
            return;
        }

        // TODO - this can be setup in the template with the cellTemplate Input on IgxColumnComponent
        columnComponent.bodyTemplate = this.getColumnBodyTemplate(column);

        if (this.hasSummary(column))
        {
            // TODO - isn't this already happening through the IgxColumnComponent.summaries binding in the template????
            columnComponent.summaries = this.getSummaryOperand(column);
        }
    }

    private getColumnBodyTemplate(column: Column): TemplateRef<IgxCellTemplateContext> | undefined
    {
        if (column.isBoolean && this.booleanTemplate)
        {
            return this.booleanTemplate;
        }

        if (column?.isIcon && this.iconTemplate)
        {
            return this.iconTemplate;
        }

        if (column?.isChip && this.chipTemplate)
        {
            return this.chipTemplate;
        }
    }

    toggleTableOptions(): void
    {
        this.tableOptionsOpen = !this.tableOptionsOpen;
    }

    private getGridColumnDataType(element: IInfoTableElement): GridColumnDataType
    {
        const cellValueStr = this.getValueString(element?.Value);
        if (!cellValueStr)
        {
            return GridColumnDataType.String;
        }

        if (StringHelper.isBoolean(cellValueStr))
        {
            return GridColumnDataType.Boolean;
        }

        if (StringHelper.isCurrency(element?.Value))
        {
            return GridColumnDataType.Currency;
        }

        if (!element.IgnoreNumber)
        {
            if (StringHelper.parseDate(element.Value))
            {
                return GridColumnDataType.Date;
            }

            if (!isNaN(StringHelper.parseNumber(element.Value)))
            {
                return GridColumnDataType.Number;
            }

        }

        return GridColumnDataType.String;
    }

    private getInfoTable(forceReload: boolean): Promise<IInfoTable>
    {
        switch (this.tableLoadType)
        {
            case TableLoadType.HardCoded:
                return this.getHardCodedInfoTableData();
            case TableLoadType.InfoTable:
                return this.getInfoTableData(forceReload);
            default:
                throw new Error(
                    `Unsupported Edge Case: unknown table load type; was ${this.tableLoadType} | ${this.tableLoadType}`
                );
        }
    }

    // TODO - does this need to return a promise?
    private getHardCodedInfoTableData(): Promise<IInfoTable>
    {
        this.gridLayoutId = this.infoTable.ProviderId;
        // TODO - why aren't we just replacing the infoTable object? Or only using the internalCopy and avoid mutating the given object.....
        this.infoTable.Rows = this.copyAndApplyFilter(this.infoTable).Rows;

        // TODO - will the grid not apply the current sorting settings on the new dataset once it gets it?
        this.inPlaceSort(this.infoTable.Rows);

        this.selectedIds?.forEach(id =>
        {
            const row = this.infoTable.Rows.find(x => x.Id === id);
            if (row)
            {
                row.Selected = true;
            }
        });
        return new Promise((resolveFn) =>
        {

            resolveFn(this.infoTable);
        });
    }

    private getInfoTableData(forceReload: boolean): Promise<IInfoTable>
    {
        this.gridLayoutId = this.criteria.ProviderId;
        if (forceReload)
        {
            return this.$infoTableService.Get(this.criteria)
                .then((data: IInfoTable) =>
                {
                    // this.internalInfoTable = Utilities.deepCopy(data);
                    return this.copyAndApplyFilter(data);
                });
        }
        else
        {
            return Promise.resolve(this.copyAndApplyFilter(this.infoTable));
        }
    }

    private inPlaceSort(rows: Array<IInfoTableRow>): void
    {
        if (!this.grid.sortingExpressions
            || this.grid.sortingExpressions.length === 0)
        {
            return;
        }

        this.grid.sortingExpressions
            .slice()
            .reverse()
            .forEach(sortingExpression =>
            {
                const sortDirection = sortingExpression?.dir;
                rows.sort((a, b) =>
                {
                    const field = rows[0].Elements.find(x => x.NameForGrid === sortingExpression.fieldName)?.Name
                        || sortingExpression.fieldName;
                    const result = compareFields(a, b, field);
                    return sortDirection === SortingDirection.Desc
                        ? result * -1
                        : result;
                });
            });

        function compareFields(a: IInfoTableRow, b: IInfoTableRow, field: string): number
        {
            const aVal = getValue(a, field);
            const bVal = getValue(b, field);

            return compareValue(aVal, bVal);
        }

        function getValue(row: IInfoTableRow, field: string): number | string
        {
            const newLocal = row.Elements.find(e => e.Name === field);
            const value = newLocal?.Value;
            if (Utilities.IsNullOrUndefined(value))
            {
                // ? REVIEW - Fallback to sorting by RowId?
                return row.Id;
            }

            if (field.toLowerCase().includes("date"))
            {
                return new Date(value).getTime();
            }

            const valueNum = StringHelper.parseNumber(value);
            if (!isNaN(valueNum))
            {
                return valueNum;
            }

            return value.toLowerCase();
        }

        function compareValue(a: unknown, b: unknown): number
        {
            return a > b ? 1 : -1;
        }
    }

    // TODO - why aren't we relying on the Parent or Service to apply the filtering?
    private copyAndApplyFilter(tableData: IInfoTable): IInfoTable
    {
        if (!this.grid || this.grid.filteringExpressionsTree.filteringOperands.length === 0)
        {
            return tableData;
        }

        // TODO - will the grid not apply the current filters on the new dataset once it gets it?
        // ?REVIEW - why do we need a deepCopy, Sorting and Filtering shouldn't mutate list elements.
        const dataCopy: IInfoTable = Utilities.deepCopy(tableData);
        const items: Array<InfoTableDataItem> = dataCopy.Rows.map(
            row => this.convertToTableItem(row, this.columns));

        const data = this.grid.filterStrategy.filter(items, this.grid.filteringExpressionsTree);

        const rows: Array<IInfoTableRow> = data.map(element =>
        {
            return element.$$row;
        });

        dataCopy.Rows = rows;
        return dataCopy;
    }

    private getColumnName(element: IInfoTableElement, index: number): string
    {
        return element.NameForGrid || element.Name || `column_${index}`;
    }

    private convertToTableItem(row: IInfoTableRow, columns: ReadonlyArray<Column> = []): InfoTableDataItem
    {
        return row.Elements.reduce(
            (item: InfoTableDataItem, cell: IInfoTableElement, colIndex: number) =>
            {
                if (!cell.Visible || cell.Name === "Id")
                {
                    return item;
                }

                const columnName = this.getColumnName(cell, colIndex);
                const column = columns.find(x => x.name === columnName);
                if (!column)
                {
                    throw new Error(`Column not defined for element; was '${columnName}'`);
                }

                /*
                 * Checks that if the cell data is inferred as a `String`, that the entire column will be a `String` data type.
                 * was `$infoGridService.setLegacyDataTypes`
                 */
                if (column.dataType !== GridColumnDataType.String
                    && this.getGridColumnDataType(cell) === GridColumnDataType.String)
                {
                    console.warn(
                        `Detected an incorrect data type in cell value.`,
                        {
                            column: column.name,
                            prev: column.dataType,
                            cell: this.getGridColumnDataType(cell)
                        });
                    column.dataType = GridColumnDataType.String;
                }

                const fieldName = cell.NameForGrid || cell.Name || `column_${colIndex}`;
                item[fieldName] = this.getTypedValue(column, cell);

                return item;
            },
            {
                Id: row.Id,
                $$row: row,
            });
    }

    private getTypedValue(column: Column, element: IInfoTableElement): string | number | boolean | undefined
    {
        if (!element.Value)
        {
            return undefined;
        }

        switch (column?.dataType)
        {
            case GridColumnDataType.Boolean:
                return StringHelper.parseBoolean(this.getValueString(element.Value));
            // Note: for all other Column DataTypes, we should rely on the column formatter function instead of attempting to change the backing-data
            case GridColumnDataType.Currency:
                return StringHelper.parseNumber(element.Value);
            case GridColumnDataType.Number:
                if (element.IgnoreNumber)
                {
                    return element.Value.toString();
                }

                return StringHelper.parseNumber(element.Value);
            default:
                return element.Value;
        }
    }

    onFetchTable(): void
    {
        this.fetch({
            forceReload: true,
            initial: true,
            page: this.currentPage,
            perPage: this.perPage
        });
    }

    onEditRow($event: EditRowArgs): void
    {
        if (EventEmitterHelper.hasSubscribers(this.editRow))
        {
            this.editRow.emit($event);
            return;
        }

        this.$infoTableDialogService.showEdit(
            {
                criteria: {
                    Id: $event.row.Id,
                    ProviderId: $event.providerId,
                    Criteria: undefined,
                    ParentId: undefined,
                },
                allowAttachments: this.infoTable.AllowAttachments,
                preventSaveAndNew: this.infoTable.PreventSaveAndNew,
                allowNotes: this.infoTable.AllowNotes,
                entityName: this.infoTable.EntityName,
                hideNoteSubject: this.infoTable.HideNoteSubject,
                viewOnly: $event.viewOnly,
                canDelete: $event.canDelete,
                infoTable: this.infoTable,
            })
            .then(doDelete =>
            {
                if (doDelete)
                {
                    this.onDeleteRow($event.row);
                }
                else
                {
                    this.fetch({
                        forceReload: true,
                        initial: true,
                        page: this.currentPage,
                        perPage: this.perPage
                    });
                }
            });
    }

    onDeleteRow($event: IInfoTableRow & AugmentedModelObject): void
    {
        if (EventEmitterHelper.hasSubscribers(this.deleteRow))
        {
            this.deleteRow.emit({ row: $event });
        }
        else
        {
            this.$infoTableService.PerformAction(
                {
                    ProviderId: this.providerId,
                    Action: {
                        Action: "delete"
                    },
                    Rows: [
                        $event
                    ],
                })
                .then(
                    _success =>
                    {
                        this.fetch({
                            forceReload: true,
                            initial: true,
                            page: this.currentPage,
                            perPage: this.perPage
                        });
                    },
                    (error: unknown) =>
                    {
                        this.$commonDialogsService.showAlertHtmlMessage(
                            isHttpResponseError(error)
                                ? error.error.Message
                                : StringHelper.stringify(error),
                            "Record Cannot Be Deleted");
                    }
                );
        }

    }

    onReloadTableData($event: FetchOptions): void
    {
        this.fetch($event);
    }

    private getValueString(value: unknown): string
    {
        if (!value)
        {
            return undefined;
        }

        if (typeof value === "object")
        {
            // TODO - what Type are we trying to assume here?
            return value["Code"];
        }

        return value.toString();
    }
}
